import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2 } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../../lib/api'
import { currentLocaleTag } from '../../lib/formatting'
import { formatCents } from '../../lib/money'
import type { InvoiceSummaryResponse } from '@jr2os/shared'

/**
 * El gráfico de «Emitidas»: lo emitido (base) y lo cobrado mes a mes, con los mismos filtros que el listado.
 *
 * Cuenta con la regla de gestión del servidor (`GET /invoices/summary`): sin borradores ni anuladas, de modo que las
 * cifras coinciden con las de la previsión y el panel. Sin año elegido enseña los últimos 12 meses.
 */
export default function InvoicesChart({ query }: { query: string }) {
  const { t } = useTranslation('billing')
  const [data, setData] = useState<InvoiceSummaryResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    api
      .get<InvoiceSummaryResponse>(`/invoices/summary${query ? `?${query}` : ''}`)
      .then((resp) => {
        if (!cancelled) setData(resp)
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setData(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [query])

  const tag = currentLocaleTag()
  const monthLabel = (m: string) =>
    new Date(`${m}-01T00:00:00Z`).toLocaleDateString(tag, {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    })
  const dayLabel = (d: string) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString(tag, { timeZone: 'UTC' })

  if (loading && !data) {
    return (
      <div className="surface p-6 text-center t-muted text-sm">
        <Loader2 size={14} className="inline-block animate-spin mr-2" />
        {t('issued.loading')}
      </div>
    )
  }
  if (error) {
    return (
      <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
        <AlertCircle size={14} /> {t('issued.error')}
      </div>
    )
  }
  if (!data) return null

  // Todo SIN IVA: lo emitido (base), lo cobrado y lo que falta por cobrar de esa base. El IVA y el total con IVA ya salen en el listado.
  const kpis: Array<{ label: string; value: string }> = [
    { label: t('issued.kpiCount'), value: String(data.totals.count) },
    { label: t('issued.kpiBase'), value: formatCents(data.totals.baseCents) },
    { label: t('issued.kpiPaid'), value: formatCents(data.totals.collectedBaseCents) },
    { label: t('issued.kpiOutstanding'), value: formatCents(data.totals.outstandingBaseCents) },
  ]

  return (
    <div className="surface p-4 space-y-3" data-testid="invoices-chart">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold t-primary">{t('issued.chartTitle')}</h3>
        <span className="text-xs t-faint">
          {t('issued.range', { from: dayLabel(data.from), to: dayLabel(data.to) })}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-base px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider t-muted">{k.label}</div>
            <div className="font-mono text-sm t-primary mt-0.5">{k.value}</div>
          </div>
        ))}
      </div>

      {data.totals.count === 0 ? (
        <p className="text-sm t-muted text-center py-6">{t('issued.empty')}</p>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.months}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="month"
              tickFormatter={monthLabel}
              tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
              tickFormatter={(v: number) => formatCents(v)}
              width={90}
            />
            <Tooltip
              labelFormatter={(m: string) => monthLabel(m)}
              formatter={(value: number | string) => formatCents(Number(value))}
              contentStyle={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="baseCents" fill="#8b5cf6" name={t('issued.legendBase')} maxBarSize={72} />
            <Bar
              dataKey="collectedBaseCents"
              fill="#10b981"
              name={t('issued.legendCollected')}
              maxBarSize={72}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
